
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

import vSelect from "vue-select"
import SelectableFormControl from "@/components/Controls/SelectableFormControl.vue"

import { Field, CaseDetails, User, CaseUser, Team } from "@/types"

import api from "@/services/api"

import { smeType, SmeLabel, smeLabels } from "@/types/sme"
import { TenantFeatures } from "@/types/tenantFeatures"
import { FieldTypeId } from "@/types/fieldTypes"
import { CalculationType } from "@/types/field"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"selectable-control": SelectableFormControl
	}
})
export default class UserRoleSetup extends Vue {
	@Prop()
	value: any;

	@Prop({ default: () => [] })
	fields!: Field[]

	@setup.State teams!: Team[]
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getCaseName!: string

	TenantFeatures = TenantFeatures

	get subroles (): any[] {
		return (this.userRole && this.userRole.subroles) ? this.userRole.subroles : []
	}

	get locationTrackingEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserRoleLocation)
	}

	get assignmentDatePerRoleFieldEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.TimeManagementAssignmentDatePerRoleField)
	}

	get numericFields () {
		return this.fields.filter(f => f.dataType === FieldTypeId.Number)
	}

	get dateFields (): Field[] {
		const dateFields = this.fields.filter(f => f.dataType === FieldTypeId.Date || f.dataType === FieldTypeId.DateTime)
		const calculatedDateFields = this.fields.filter(f =>
			f.dataType === FieldTypeId.Calculated && f.extensions?.["calculationType"] === CalculationType.AddToDate
		)

		return dateFields.concat(calculatedDateFields)
	}

	mounted () {
		if (!this.value) {
			this.userRole = {
				hasSubroles: false,
				subroles: []
			}
			return
		}

		this.userRole = { ...this.value }

		if (this.userRole.hasSubroles === undefined) {
			this.raiseChange({ hasSubroles: false, subroles: [] })
		} else if (this.userRole.subroles === undefined) {
			this.raiseChange({ subroles: [] })
		}
	}

	set userRole (userRole: any) {
		this.$emit("input", {
			...userRole
		})
	}

	get userRole () {
		return this.value
	}

	private get userRoleTimeManagementHours () {
		const allowMultiHoursFields = this.getIsFeatureEnabled(TenantFeatures.AllowMultipleHourFieldsForTimeManagementEnabledUserRole)
		const hoursFields = allowMultiHoursFields
			? this.userRole.timeManagementHours
			: this.userRole.timeManagementHours?.[0]

		return hoursFields ?? null
	}

	private set userRoleTimeManagementHours (newValue: string | string[] | null | undefined) {
		this.userRole.timeManagementHours = newValue
		if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
			this.userRole.timeManagementAssignmentDate = null
		}
	}

	private get hasTimeManagementHoursSet (): boolean {
		return Array.isArray(this.userRoleTimeManagementHours)
			? this.userRoleTimeManagementHours.length > 0
			: !!this.userRoleTimeManagementHours
	}

	raiseChange (changedData: any) {
		// Clear the relevant data depending on if subroles is enabled
		if (!this.userRole.hasSubroles && this.userRole.subroles) {
			this.userRole.subroles = []
		} else {
			delete this.userRole.isSingleUser
			delete this.userRole.makeAssignee
			delete this.userRole.allowTimeTracking
			delete this.userRole.allowLocationTracking
		}

		if (!this.userRole.isSingleUser || this.userRole.allowExternalUsers) {
			this.userRole.makeAssignee = false
		}

		this.userRole = {
			...this.userRole,
			...changedData
		}
	}

	addSubRole () {
		this.userRole.subroles.push({
			title: "",
			isSingleUser: false,
			makeAssignee: false,
			allowTimeTracking: false
		})
	}

	removeSubRole (role: any) {
		this.userRole.subroles.remove(role)
	}

	singleUserSet (value : any, role: any) {
		if (!value) { role.makeAssignee = false }
	}

	setTeams (teams: string[]) {
		this.raiseChange({
			teams
		})
	}

	setSme (smeType: smeType | null) {
		this.raiseChange({
			smeType
		})
	}

	get smeLabels (): SmeLabel[] {
		return smeLabels
	}

	get reviewerAutomationEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ReviewerAutomation)
	}

	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}

	setTimeHours (timeManagementHours: string[] | string | null) {
		let updatedTimeManagementHours = typeof timeManagementHours === "string" ? [timeManagementHours] : timeManagementHours
		this.userRoleTimeManagementHours = updatedTimeManagementHours
		this.raiseChange({
			timeManagementHours: updatedTimeManagementHours
		})
	}

	allowTimeTrackingChanged (enabled: boolean, role: any) {
		if (!enabled) {
			role.allowLocationTracking = false
		}
	}
}

